import { db, doc, getDoc } from "@/firebase";
import type { SummaryRecord } from "@/services/summaryService";
import type { FlashcardRecord } from "@/services/flashcardService";
import { logDebug, logInfo, logError, logSuccess } from "@/utils/secureLogger";
import katex from "katex";

interface AIStudyMaterial {
  id: string;
  title: string;
  type: "Summary" | "Flashcards" | "Quiz";
  subject: string;
  source: string;
  date: Date | string;
  description: string;
  keyPoints: string;
  keyPointsList?: string[];
  flashcards?: Array<{ front: string; back: string }>;
  sources?: string[];
}

class HTMLPDFExportService {
  /**
   * Format date to readable string
   */
  private formatDate(date: Date | string): string {
    const dateObj = date instanceof Date ? date : new Date(date);

    const monthNames = [
      "Jan.",
      "Feb.",
      "Mar.",
      "Apr.",
      "May",
      "Jun.",
      "Jul.",
      "Aug.",
      "Sept.",
      "Oct.",
      "Nov.",
      "Dec.",
    ];

    const month = monthNames[dateObj.getMonth()];
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();

    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${month} ${day}, ${year} - ${hours}:${minutes}${ampm}`;
  }

  /**
   * Process text to render LaTeX/math formulas using KaTeX
   */
  private processFormulas(text: string): string {
    // Match LaTeX inline formulas: $...$
    let processed = text.replace(/\$([^$]+)\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula, {
          throwOnError: false,
          displayMode: false,
        });
      } catch (error) {
        logDebug("KaTeX inline formula parsing error", { formula, error });
        return match; // Return original if parsing fails
      }
    });

    // Match LaTeX display formulas: $$...$$
    processed = processed.replace(/\$\$([^$]+)\$\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula, {
          throwOnError: false,
          displayMode: true,
        });
      } catch (error) {
        logDebug("KaTeX display formula parsing error", { formula, error });
        return match;
      }
    });

    return processed;
  }

  /**
   * Escape HTML special characters
   * Note: This is a safe use of innerHTML - textContent automatically escapes HTML,
   * then innerHTML returns the escaped version. This is a standard sanitization pattern.
   */
  private escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text; // Safely escapes HTML entities
    return div.innerHTML; // Returns the escaped HTML
  }

  /**
   * Fetch full summary data from Firebase
   */
  private async fetchFullSummaryData(summaryId: string): Promise<SummaryRecord | null> {
    try {
      logInfo("Fetching full summary data for PDF export", {
        summaryId: summaryId.substring(0, 8) + "***",
      });

      const summaryDoc = await getDoc(doc(db, "summaries", summaryId));

      if (summaryDoc.exists()) {
        const data = summaryDoc.data() as Omit<SummaryRecord, "summary_id">;
        const fullData = {
          summary_id: summaryDoc.id,
          ...data,
        };
        logSuccess("Full summary data fetched for PDF export");
        return fullData;
      } else {
        logError("Summary document not found for PDF export", {
          summaryId: summaryId.substring(0, 8) + "***",
        });
        return null;
      }
    } catch (error) {
      logError("Error fetching full summary data for PDF export", error);
      return null;
    }
  }

  /**
   * Fetch full flashcard data from Firebase for PDF export
   */
  private async fetchFullFlashcardData(flashcardId: string): Promise<FlashcardRecord | null> {
    try {
      logInfo("Fetching full flashcard data for PDF export", {
        flashcardId: flashcardId.substring(0, 8) + "***",
      });

      const flashcardDoc = await getDoc(doc(db, "flashcards", flashcardId));

      if (flashcardDoc.exists()) {
        const data = flashcardDoc.data() as Omit<FlashcardRecord, "flashcards_id">;
        const fullData = {
          flashcards_id: flashcardDoc.id,
          ...data,
        };
        logSuccess("Full flashcard data fetched for PDF export", {
          flashcardCount: fullData.flashcard_data?.length || 0,
        });
        return fullData;
      } else {
        logError("Flashcard document not found for PDF export", {
          flashcardId: flashcardId.substring(0, 8) + "***",
        });
        return null;
      }
    } catch (error) {
      logError("Error fetching full flashcard data for PDF export", error);
      return null;
    }
  }

  /**
   * Generate HTML content for PDF
   */
  private generateHTML(exportData: AIStudyMaterial, isFlashcard: boolean = false): string {
    const formattedDate = this.formatDate(exportData.date);

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${this.escapeHtml(exportData.title)}</title>
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.18/dist/katex.min.css">
  <style>
    /* Font Imports - Math-ready fonts */
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Noto+Sans+Math&display=swap');
    
    /* General Reset & Base Styles */
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: 'Inter', 'Noto Sans Math', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 11pt;
      line-height: 1.65;
      color: #1a1a1a;
      background: white;
      padding: 15mm 20.32mm 10mm 20.32mm; /* 0.8 inches margins */
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    /* Print-specific styles */
    @media print {
      body {
        padding: 0;
        margin: 0;
      }

      .page {
        page-break-after: always;
        padding: 15mm 20.32mm 10mm 20.32mm; /* 0.8 inches margins */
        min-height: 257mm; /* A4 height minus margins */
      }

      .page:last-child {
        page-break-after: auto;
      }

      .no-break {
        page-break-inside: avoid;
      }

      /* Prevent overflow */
      * {
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
      }

      /* Math formulas special handling */
      .katex-display {
        overflow-x: auto;
        overflow-y: hidden;
        max-width: 100%;
      }

      .katex {
        font-size: 1.05em;
      }
    }

    @page {
      size: A4;
      margin: 15mm 20.32mm 10mm 20.32mm; /* top right bottom left - 0.8 inches = 20.32mm */
    }

    /* Header Styles - Polished */
    .header {
      text-align: center;
      margin-bottom: 6mm;
      padding-bottom: 5mm;
      padding-top: 3mm;
      border-bottom: 1px solid #d1d5db;
      background: #ffffff;
    }

    .header h1 {
      font-size: 20pt;
      font-weight: 700;
      margin-bottom: 4mm;
      color: #000;
      letter-spacing: -0.3px;
      text-transform: uppercase;
    }

    .ai-notice {
      background: #fff9e6;
      border: 1px solid #ff8c00;
      padding: 2mm 4mm;
      margin: 3mm auto;
      border-radius: 2mm;
      max-width: 85%;
    }

    .ai-notice-title {
      color: #d97706;
      font-weight: 700;
      font-size: 8.5pt;
      margin-bottom: 1.5mm;
      letter-spacing: 0.3px;
      text-transform: uppercase;
    }

    .ai-notice-text {
      color: #92400e;
      font-size: 7.5pt;
      line-height: 1.4;
      font-weight: 500;
    }

    /* Content Styles - Professional */
    .title {
      font-size: 20pt;
      font-weight: 700;
      margin: 6mm 0 4mm 0;
      padding: 4mm 0;
      color: #000;
      letter-spacing: -0.3px;
      line-height: 1.4;
      border-left: 5px solid #3b82f6;
      padding-left: 4mm;
      background: linear-gradient(90deg, #eff6ff 0%, transparent 100%);
    }

    .metadata {
      color: #505050;
      font-size: 10.5pt;
      margin-bottom: 6mm;
      padding: 4mm 5mm;
      border: 1px solid #e0e0e0;
      border-radius: 3mm;
      background: #f9fafb;
      font-weight: 500;
    }

    .metadata-item {
      margin: 2.5mm 0;
      display: flex;
      align-items: baseline;
    }

    .metadata-item strong {
      color: #1f2937;
      font-weight: 700;
      min-width: 90px;
      display: inline-block;
    }

    .section-title {
      font-size: 16pt;
      font-weight: 700;
      margin-top: 0;
      margin-bottom: 6mm;
      padding: 3mm 0 3mm 4mm;
      color: #1e40af;
      letter-spacing: -0.2px;
      border-left: 5px solid #3b82f6;
      background: linear-gradient(90deg, #dbeafe 0%, transparent 100%);
      page-break-after: avoid;
    }

    .section-page {
      page-break-before: always;
      padding-top: 5mm;
    }

    .summary-text {
      text-align: justify;
      margin-bottom: 6mm;
      line-height: 1.8;
      color: #1a1a1a;
      font-size: 11pt;
      padding: 0;
      background: #ffffff;
    }

    .key-points {
      margin: 6mm 0;
    }

    .key-point {
      margin: 3mm 0 3mm 8mm;
      padding: 2mm 0;
      position: relative;
      line-height: 1.7;
      text-align: justify;
      background: transparent;
      border: none;
    }

    .key-point::before {
      content: "•";
      position: absolute;
      left: -6mm;
      font-size: 12pt;
      color: #000000;
      line-height: 1;
    }

    /* Flashcard Styles - Modern Cards */
    .flashcards-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 7mm;
      margin: 6mm 0;
    }

    .flashcard {
      page-break-inside: avoid;
      margin-bottom: 8mm;
    }

    .card-number {
      color: #059669;
      font-weight: 700;
      font-size: 11.5pt;
      margin-bottom: 3.5mm;
      padding: 2mm 3mm;
      background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
      border-radius: 2mm;
      display: inline-block;
    }

    .card-side {
      border: 2.5px solid #d0d0d0;
      padding: 5mm;
      margin-bottom: 3.5mm;
      min-height: 24mm;
      border-radius: 3mm;
      box-shadow: 0 2px 4px rgba(0,0,0,0.12);
      transition: all 0.3s ease;
    }

    .card-side.front {
      border-color: #4b5563;
      background: linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%);
    }

    .card-side.back {
      border-color: #10b981;
      background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
    }

    .card-label {
      font-size: 9pt;
      font-weight: 700;
      margin-bottom: 3mm;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      padding-bottom: 2mm;
      border-bottom: 2px solid currentColor;
    }

    .card-label.front {
      color: #374151;
    }

    .card-label.back {
      color: #059669;
    }

    .card-content {
      font-size: 10.5pt;
      line-height: 1.65;
      color: #1a1a1a;
    }

    /* Sources Section - Cleaner */
    .sources-page {
      page-break-before: always;
      padding-top: 5mm;
    }
    
    .sources {
      margin-top: 6mm;
    }

    .source-item {
      margin: 3mm 0;
      padding: 2mm 0;
      text-align: justify;
      line-height: 1.6;
      position: relative;
      background: transparent;
      border: none;
    }

    .source-item strong {
      color: #000000;
      font-weight: 600;
      font-size: 10.5pt;
    }

    /* Footer Styles - Professional */
    .footer {
      position: fixed;
      bottom: 3mm;
      left: 20.32mm;
      right: 20.32mm;
      text-align: center;
      font-size: 8pt;
      color: #6b7280;
      border-top: 2px solid #e5e7eb;
      padding-top: 2mm;
      background: linear-gradient(180deg, transparent 0%, #ffffff 20%);
    }

    @media print {
      .footer {
        position: fixed;
        bottom: 3mm;
      }
    }

    .footer-disclaimer {
      color: #dc2626;
      font-weight: 700;
      margin-bottom: 1.5mm;
      font-size: 9pt;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .footer-text {
      color: #6b7280;
      font-size: 7.5pt;
      font-weight: 500;
      line-height: 1.5;
    }

    /* Math formula handling - Enhanced */
    .katex {
      font-size: 1.05em !important;
    }

    .katex-display {
      margin: 4mm 0;
      overflow-x: auto;
      padding: 3mm 0;
    }

    /* Special character support */
    .math-symbol, .greek-letter {
      font-family: 'Noto Sans Math', 'Cambria Math', serif;
    }

    /* Print refinements */
    @media print {
      body {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
      }
    }

    /* Content wrapper to avoid header/footer overlap */
    .content-wrapper {
      padding-bottom: 12mm; /* Space for footer */
      min-height: calc(100vh - 25mm); /* Ensure content area is clear of footer */
    }

    /* Page number styling */
    .page-number {
      position: fixed;
      bottom: 12mm;
      right: 15mm;
      font-size: 9pt;
      color: #9ca3af;
      font-weight: 600;
    }
  </style>
</head>
<body>
  <div class="content-wrapper">
    <!-- Header -->
    <div class="header">
      <h1>AI-Generated ${isFlashcard ? "Flashcards" : "Study Material"}</h1>
      <div class="ai-notice">
        <div class="ai-notice-title">⚠️ AI-Generated Content - For Reference Only</div>
        <div class="ai-notice-text">
          This content was generated by AI and may contain errors or inaccuracies.<br>
          Always verify information with trusted sources.
        </div>
      </div>
    </div>

    <!-- Title and Metadata -->
    <div class="title">${this.processFormulas(this.escapeHtml(exportData.title))}</div>
    <div class="metadata">
      <div class="metadata-item"><strong>Subject:</strong> ${this.escapeHtml(
        exportData.subject
      )}</div>
      <div class="metadata-item"><strong>Generated:</strong> ${formattedDate}</div>
      ${
        isFlashcard && exportData.flashcards
          ? `<div class="metadata-item"><strong>Total Cards:</strong> ${exportData.flashcards.length}</div>`
          : ""
      }
    </div>

    <!-- Main Content -->
    ${
      !isFlashcard
        ? this.generateSummaryContent(exportData)
        : this.generateFlashcardContent(exportData)
    }

    <!-- Sources (if any) -->
    ${
      exportData.sources && exportData.sources.length > 0
        ? this.generateSourcesSection(exportData.sources)
        : ""
    }
  </div>

  <!-- Footer -->
  <div class="footer">
    <div class="footer-disclaimer">⚠️ AI Content Notice</div>
    <div class="footer-text">
      AI-generated content for reference only.
      Always verify with trusted sources. <br> Generated with Google Gemini ${
        isFlashcard ? "2.5-flash" : "2.5 Pro"
      } (Oct 2025)
    </div>
  </div>

  <script>
    // Wait for all resources to load before printing
    window.addEventListener('load', function() {
      // Small delay to ensure KaTeX rendering is complete
      setTimeout(function() {
        console.log('PDF ready for printing');
      }, 500);
    });
  </script>
</body>
</html>
    `;
  }

  /**
   * Generate summary content HTML
   */
  private generateSummaryContent(exportData: AIStudyMaterial): string {
    let html = `
    <!-- Summary Section - Start on first page, can flow to next -->
    <div>
      <div class="section-title">Summary</div>
      <div class="summary-text">
        ${this.processFormulas(this.escapeHtml(exportData.description))}
      </div>
    </div>
    `;

    // Key Points - Separate Page
    if (exportData.keyPointsList && exportData.keyPointsList.length > 0) {
      html += `
    <div class="section-page">
      <div class="section-title">Key Points</div>
      <div class="key-points">
        ${exportData.keyPointsList
          .map(
            (point) => `
          <div class="key-point">
            ${this.processFormulas(this.escapeHtml(point))}
          </div>
        `
          )
          .join("")}
      </div>
    </div>
      `;
    }

    return html;
  }

  /**
   * Generate flashcard content HTML
   */
  private generateFlashcardContent(exportData: AIStudyMaterial): string {
    if (!exportData.flashcards || exportData.flashcards.length === 0) {
      return "<p>No flashcards available.</p>";
    }

    let html = '<div><div class="section-title">Flashcards</div><div class="flashcards-grid">';

    exportData.flashcards.forEach((flashcard, index) => {
      html += `
      <div class="flashcard">
        <div class="card-number">Card ${index + 1}</div>
        <div class="card-side front">
          <div class="card-label front">Question</div>
          <div class="card-content">
            ${this.processFormulas(this.escapeHtml(flashcard.front))}
          </div>
        </div>
        <div class="card-side back">
          <div class="card-label back">Answer</div>
          <div class="card-content">
            ${this.processFormulas(this.escapeHtml(flashcard.back))}
          </div>
        </div>
      </div>
      `;
    });

    html += "</div></div>";
    return html;
  }

  /**
   * Format a source URL into APA 7th Edition citation format
   */
  private formatSourceAPA(url: string): string {
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace("www.", "");

      // Extract author from domain
      let author = domain.split(".")[0];

      // Special handling for .edu/.gov domains
      if (domain.includes(".edu") || domain.includes(".gov")) {
        // Extract meaningful author from subdomain/domain
        const parts = domain.split(".");
        if (parts.length >= 2) {
          author = parts[0];
        }
      }

      // Capitalize first letter
      author = author.charAt(0).toUpperCase() + author.slice(1);

      // Extract title from URL path
      let title = "Educational resource";
      const pathSegments = urlObj.pathname.split("/").filter((s) => s);
      if (pathSegments.length > 0) {
        const lastSegment = pathSegments[pathSegments.length - 1];
        title = lastSegment.replace(/[-_]/g, " ").replace(/\.(pdf|html|htm)$/i, "");
        if (title.length > 50) {
          title = title.substring(0, 50) + "...";
        }
      }

      // Format retrieval date (full date format)
      const now = new Date();
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const retrievalDate = `${monthNames[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;

      // APA 7th Edition format: Author. Title. Retrieved Date, from URL
      return `<span style="font-weight: 600;">${this.escapeHtml(
        author
      )}.</span> <span style="font-style: italic;">${this.escapeHtml(
        title
      )}</span>. Retrieved ${retrievalDate}, from <a href="${this.escapeHtml(
        url
      )}" style="color: #2563eb; text-decoration: none;">${this.escapeHtml(url)}</a>`;
    } catch (error) {
      // Fallback to plain URL if parsing fails
      return `<a href="${this.escapeHtml(
        url
      )}" style="color: #2563eb; text-decoration: none;">${this.escapeHtml(url)}</a>`;
    }
  }

  /**
   * Generate sources section HTML (separate page)
   */
  private generateSourcesSection(sources: string[]): string {
    return `
  <div class="sources-page">
    <div class="section-title">Sources & References</div>
    <div class="ai-notice">
      <div class="ai-notice-title">⚠️ AI Content Notice</div>
      <div class="ai-notice-text">
        The content provided was generated by AI and cross-referenced among the additional sources below.<br>
        Always verify information independently for academic use.
      </div>
    </div>
    <div class="sources">
      ${sources
        .map(
          (source, index) => `
        <div class="source-item">
          <strong>${index + 1}.</strong> ${this.formatSourceAPA(source)}
        </div>
      `
        )
        .join("")}
    </div>
  </div>
    `;
  }

  /**
   * Export AI Study Material to PDF using browser print (via hidden iframe)
   */
  async exportMaterialToPDF(material: AIStudyMaterial): Promise<void> {
    try {
      logInfo("Starting HTML PDF export process", {
        materialId: material.id.substring(0, 8) + "***",
        type: material.type,
      });

      // Handle Flashcards type separately
      if (material.type === "Flashcards") {
        logInfo("Detected Flashcards type, fetching flashcard data and using flashcard export");
        const flashcardData = await this.fetchFullFlashcardData(material.id);
        if (flashcardData) {
          const flashcardExportData: AIStudyMaterial = {
            ...material,
            flashcards: flashcardData.flashcard_data,
            sources: flashcardData.sources,
          };
          return this.exportFlashcardsToPDF(flashcardExportData);
        } else {
          throw new Error("Failed to fetch flashcard data from Firebase");
        }
      }

      // Fetch full data if it's a Summary type
      let exportData = material;
      if (material.type === "Summary") {
        const fullSummaryData = await this.fetchFullSummaryData(material.id);
        if (fullSummaryData) {
          exportData = {
            ...material,
            description: fullSummaryData.summary_text,
            keyPointsList: fullSummaryData.key_points,
            sources: fullSummaryData.sources,
          };
        }
      }

      // Generate HTML
      const htmlContent = this.generateHTML(exportData, false);

      // Create hidden iframe for printing
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow?.document;
      if (!iframeDoc) {
        throw new Error("Failed to create print iframe");
      }

      // Write HTML content to iframe
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();

      // Wait for resources to load, then trigger print
      iframe.contentWindow?.addEventListener("load", () => {
        setTimeout(() => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          logSuccess("PDF export initiated successfully");

          // Clean up iframe after print dialog closes
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        }, 700);
      });
    } catch (error) {
      logError("Error during PDF export", error);
      throw new Error(
        `Failed to export PDF: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Export Flashcards to PDF using browser print (via hidden iframe)
   */
  async exportFlashcardsToPDF(material: AIStudyMaterial): Promise<void> {
    try {
      logInfo("Starting Flashcard HTML PDF export process", {
        materialId: material.id.substring(0, 8) + "***",
        flashcardCount: material.flashcards?.length || 0,
      });

      if (!material.flashcards || material.flashcards.length === 0) {
        throw new Error("No flashcards available for export");
      }

      // Generate HTML
      const htmlContent = this.generateHTML(material, true);

      // Create hidden iframe for printing
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow?.document;
      if (!iframeDoc) {
        throw new Error("Failed to create print iframe");
      }

      // Write HTML content to iframe
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();

      // Wait for resources to load, then trigger print
      iframe.contentWindow?.addEventListener("load", () => {
        setTimeout(() => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          logSuccess("Flashcard PDF export initiated successfully");

          // Clean up iframe after print dialog closes
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        }, 700);
      });
    } catch (error) {
      logError("Error during Flashcard PDF export", error);
      throw new Error(
        `Failed to export flashcards PDF: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }
}

// Export singleton instance
export const htmlPdfExportService = new HTMLPDFExportService();
export default htmlPdfExportService;
